import { FormikHelpers } from 'formik';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAddToCartPixel } from '@/resources/hooks/useAddToCartPixel';
import { useCartAction } from '@/resources/hooks/useCartAction';
import { useCustomizeProduct } from '@/resources/hooks/useCustomizeProduct';
import { addCartItem, changeItem } from '@/routes/order';
import { ClientTypes } from '@/services/clients';
import { setCartAlertAfterBuy } from '@/store/ducks/customer/actions';
import { ICartItemValues, ICartProduct, IOrder } from '@/store/ducks/order/types';
import { IProduct } from '@/store/ducks/products/types';

import { normalizeFormValues } from './formUtils';

interface UseCartHandlersProps {
  order: IOrder | null;
  product: IProduct | ICartProduct | null;
  action: 'add' | 'receive';
  getInitialQuantity: () => number;
  currentMeasure: 'kg' | 'unity';
  optionsGroup: any;
  blockedMedicine: boolean;
  configs: any;
  selectCartAlertWasAskedAfterBuy: boolean;
  setShowIdenfificationModal: (show: boolean) => void;
  setShowCartAlertModal: (show: boolean) => void;
  optionModal: any;
}

export const useCartHandlers = ({
  order,
  product,
  action,
  getInitialQuantity,
  currentMeasure,
  optionsGroup,
  blockedMedicine,
  configs,
  selectCartAlertWasAskedAfterBuy,
  setShowIdenfificationModal,
  setShowCartAlertModal,
  optionModal,
}: UseCartHandlersProps) => {
  const dispatch = useDispatch();

  const findItemByOrder = (order: IOrder | null) => {
    if (!order || !product) return null;

    return order.cart.products.find(
      (item) => item.product_id === product.product_id && item.ean === product.ean,
    );
  };

  const callGTagEvents = (order: IOrder | null, oldOrder: IOrder | null) => {
    if (!order || !product) return;

    const oldItem = findItemByOrder(oldOrder);
    const newItem = findItemByOrder(order);

    if (!newItem) {
      useCartAction('remove_from_cart', oldItem!, oldItem!.quantity);
      return;
    }

    const event =
      !oldItem || oldItem.quantity < newItem?.quantity ? 'add_to_cart' : 'remove_from_cart';

    if (!oldItem) {
      if (newItem.data?.spec) {
        useCustomizeProduct(newItem, newItem.quantity, JSON.stringify(newItem?.data?.spec));
      }
      useAddToCartPixel(newItem, newItem.quantity);
    }
    useCartAction(event, newItem, newItem.quantity);
  };

  const hasToShowCartAlert = (order: IOrder | null) => {
    return (
      action === 'add' &&
      configs.has_cart_alert &&
      !selectCartAlertWasAskedAfterBuy &&
      order?.cart?.products?.length === 1
    );
  };

  const normalizeValues = (cartItem: ICartItemValues) => {
    return normalizeFormValues(
      cartItem,
      action,
      getInitialQuantity,
      currentMeasure,
      product?.see_as_unit ?? false,
      optionsGroup,
    );
  };

  const handleAddCartItem = async (
    orderId: number,
    cartItem: ICartItemValues,
  ): Promise<IOrder | null> => {
    const values = normalizeValues(cartItem);
    const oldOrder = order ? { ...order } : null;

    return addCartItem(orderId, values).then((order) => {
      toast('Adicionado ao carrinho com sucesso.', { type: 'success' });
      callGTagEvents(order, oldOrder);

      if (order?.client.type === ClientTypes.ANONYMOUS && !blockedMedicine) {
        optionModal.toggle();
        setShowIdenfificationModal(true);
      } else if (hasToShowCartAlert(order)) {
        setShowCartAlertModal(true);
        dispatch(setCartAlertAfterBuy(true));
      }

      return order;
    });
  };

  const handleChangeItem = async (
    orderId: number,
    cartItem: ICartItemValues,
  ): Promise<IOrder | null> => {
    const values = normalizeValues(cartItem);
    const oldOrder = order ? { ...order } : null;

    return changeItem(orderId, values).then((order) => {
      toast('Carrinho atualizado com sucesso.', { type: 'success' });
      callGTagEvents(order, oldOrder);

      return order;
    });
  };

  const handleSubmit = async (
    cartItem: ICartItemValues,
    formik: FormikHelpers<ICartItemValues>,
  ): Promise<IOrder | null> => {
    const callSubmitRequest = action === 'add' ? handleAddCartItem : handleChangeItem;
    formik.setSubmitting(true);

    return callSubmitRequest(order!.id, cartItem)
      .catch((err) => {
        console.error(err);
        toast(err.message, { type: err.cause ?? 'error' });

        return order;
      })
      .finally(() => formik.setSubmitting(false));
  };

  return {
    handleSubmit,
    handleAddCartItem,
    handleChangeItem,
  };
};
